import { Injectable } from "@angular/core";
import { HttpClient, HttpEventType, HttpHeaders } from "@angular/common/http";
import { of } from "rxjs";
import { BehaviorSubject, Observable, Subject, concat, of as observableOf } from "rxjs";
import { filter, map, switchMap, shareReplay } from "rxjs/operators";

import { StorageService, StorageKeys } from "./storage.service";
import { WindowService } from "./window.service";
import { HelperService } from "./helper.service";
import { LanguageService } from "./language.service";
import { LogService } from "./log.service";
import { saveAs } from "file-saver";
import { Notification, StorageDetails, User } from "../models";
import moment from "moment";
import { environment } from "../environments/environment";

@Injectable({
  providedIn: "root",
})
export class UserService {
  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private _win: WindowService,
    private _log: LogService,
    private _helperService: HelperService,
    private _languageService: LanguageService
  ) {
    const theme = this.storage.getItem(StorageKeys.THEME);
    this.themeImg = theme ? theme : this.user?.active_subscription ? "sphere" : "auto";
    this._helperService.userID = this.user ? this.user.id : undefined;
  }

  public notificationTopicColors: any = {
    collaboration: "#235E6E",
    community: "#00e658",
    support: "#827A3D",
    learn: "#e600cb",
    products: "#825249",
  };

  public profilePhotoCacheBuster: any;
  public genericErrorMessage: string = "We're having trouble completing your request. Please check your internet connection or try again later.";
  public shouldRedirectNonSphere: boolean = true;
  public loggingOut$: Subject<"finished" | "started"> = new Subject();
  public tokenExpired$: Subject<boolean> = new Subject();
  public userDataRefreshed$: Subject<User> = new Subject();
  public userAuthenticated$: Subject<boolean> = new Subject();
  public subscriptionDataRetrieved$: BehaviorSubject<any> = new BehaviorSubject(false);
  private _themeImg$: BehaviorSubject<string> = new BehaviorSubject(this.user?.active_subscription ? "sphere" : "auto");
  private _user: User;
  public passwordTester = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{12,24}$/;
  public emailTester = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  public news: Array<any>;
  public dataWeNeed: any = [
    {
      key: "firstName",
      title: "First Name",
      translation_key: "profile.edit.first_name",
    },
    {
      key: "lastName",
      title: "Last Name",
      translation_key: "profile.edit.last_name",
    },
    {
      key: "birthDay",
      title: "Birthday",
      translation_key: "profile.edit.birthday",
    },
    {
      key: "description",
      title: "Description",
      translation_key: "profile.edit.description",
    },
    {
      key: "photoURL",
      title: "Profile Photo",
      translation_key: "profile.edit.profile_photo",
    },
    {
      key: "country",
      title: "Country",
      translation_key: "profile.edit.country",
    },
    {
      key: "native_language",
      title: "Language",
      translation_key: "sphere.account.language",
    },
    {
      key: "skills",
      title: "Skills",
      translation_key: "com.skills_word",
    },
    {
      key: "phone",
      title: "Phone Number",
      translation_key: "account.phone",
    },
    {
      key: "description",
      title: "Description",
      translation_key: "profile.edit.description",
    },
  ];

  public canadianProvinces = {
    AB: "Alberta",
    BC: "British Columbia",
    MB: "Manitoba",
    NB: "New Brunswick",
    NL: "Newfoundland and Labrador",
    NS: "Nova Scotia",
    NT: "Northwest Territories",
    NU: "Nunavut",
    ON: "Ontario",
    PE: "Prince Edward Island",
    QC: "Quebec",
    SK: "Saskatchewan",
    YT: "Yukon Territory",
  };

  public indianStates = {
    AN: "Andaman and Nicobar Islands",
    AP: "Andhra Pradesh",
    AR: "Arunachal Pradesh",
    AS: "Assam",
    BR: "Bihar",
    CH: "Chandīgarh",
    CT: "Chhattisgarh",
    DL: "Delhi",
    DH: "Dādra and Nagar Haveli and Damān and Diu",
    GA: "Goa",
    GJ: "Gujarat",
    HR: "Haryana",
    HP: "Himachal Pradesh",
    JH: "Jharkhand",
    JK: "Jammu an Kashmīr",
    KA: "Karnataka",
    KL: "Kerala",
    LD: "Lakshadweep",
    MP: "Madhya Pradesh",
    MH: "Maharashtra",
    MN: "Manipur",
    ML: "Meghalaya",
    MZ: "Mizoram",
    NL: "Nagaland",
    OR: "Odisha",
    PB: "Punjab",
    PY: "Puducherry",
    RJ: "Rajasthan",
    SK: "Sikkim",
    TN: "Tamil Nadu",
    TG: "Telangana",
    TR: "Tripura",
    UP: "Uttar Pradesh",
    UT: "Uttarakhand",
    WB: "West Bengal",
  };

  public needsToLinkFenderAccount: boolean = false;

  /**
   * Helper method to save user language preference
   * Used as a callback for LanguageService
   */
  saveUserLanguage(userId: string, languageCode: string): void {
    this.saveUser({
      id: userId,
      language_code: languageCode,
    }).subscribe(() => {});
  }

  public userSubscription: any;
  public subscriptionProvider: string;
  public shopperData: any;
  public spherePrices: any = {
    monthly: "19.99",
    annual: "179.99",
    storage: {
      monthly: "3.95",
      annual: "42.95",
    },
  };
  public isEmployeeOrDealer: boolean;
  public isEmployee: boolean;

  public userMetadataLoaded: boolean = false;
  private _userMetadata: any = {
    products_seen: {},
    sphere_announcements: {},
  };

  get userMetadata() {
    return this._userMetadata;
  }

  set userMetadata(data) {
    this._userMetadata = data;
    this.userMetadataUpdated$.next(this._userMetadata);
    this.userMetadataLoaded = true;
  }
  public userMetadataUpdated$: Subject<any> = new Subject();

  private _appId: string;
  set appId(value) {
    this._appId = value;
    if (value == "exchange") {
      this.themeImg = "darkblue";
    }
  }
  public badgeUsersMap: any;
  private _badgeUsers: any;
  get badgeUsers() {
    if (!this._badgeUsers) this._badgeUsers = this.storage.getItem(StorageKeys.BADGE_USERS);
    return this._badgeUsers;
  }
  set badgeUsers(data) {
    if (data) {
      this._badgeUsers = data;
      this.storage.setItem(StorageKeys.BADGE_USERS, data);
    } else {
      this._badgeUsers = false;
      this.storage.removeItem(StorageKeys.BADGE_USERS);
    }
  }

  get appId() {
    return this._appId;
  }

  public activeTheme: string;

  set themeImg(value) {
    if (this._appId == "exchange") {
      this._themeImg$.next("darkblue");
    } else {
      if (value) {
        this.storage.setItem(StorageKeys.THEME, value);
        this._themeImg$.next(value);
      } else {
        this.storage.removeItem(StorageKeys.THEME);
        this._themeImg$.next(undefined);
      }
    }
  }

  private _userDataReady: boolean = false;
  get userDataReady() {
    return this._userDataReady;
  }

  set userDataReady(ready) {
    this._userDataReady = ready;
  }

  get themeImg() {
    return this._themeImg$.getValue();
  }

  get themeImg$() {
    return this._themeImg$;
  }

  public dashboardRedesignedDate = new Date("2021-07-19");
  get legacyFree() {
    return new Date(this._user.createdTime) < this.dashboardRedesignedDate;
  }

  public user$: BehaviorSubject<any> = new BehaviorSubject(false);
  set user(user) {
    if (!user) {
      this.storage.removeItem(StorageKeys.USER);
    } else {
      this.isEmployeeOrDealer = this.checkEmployeeOrDealerAccess(user);
      this.isEmployee = this.checkEmployeeAccess(user);
      if (user && !user.name) this.storage.setItem(StorageKeys.USER, user);
      if (user && user.theme && this.themeImg != user.theme) this.themeImg = user.theme;
    }
    this._user = user;
    if (user && user.fender_cognito_user_details && !user.fender_cognito_user_id) {
      this.needsToLinkFenderAccount = true;
    }
    this.user$.next(user);
  }

  get user(): any {
    if (this._user) {
      if (!this.shopperData && this._user.shopper_data) this._processShopperData(this._user.shopper_data);
      this.isEmployeeOrDealer = this.checkEmployeeOrDealerAccess(this._user);
      this.isEmployee = this.checkEmployeeAccess(this._user);
      return this._user;
    } else {
      let storedUser = this.storage.getItem(StorageKeys.USER);
      if (storedUser) {
        this._user = storedUser;
        this.isEmployeeOrDealer = this.checkEmployeeOrDealerAccess(this._user);
        this.isEmployee = this.checkEmployeeAccess(this._user);
        if (!this.shopperData && this._user.shopper_data) this._processShopperData(this._user.shopper_data);
        return storedUser;
      } else {
        return undefined;
      }
    }
  }

  private _cookieUser;
  set cookieUser(user) {
    this._cookieUser = user;
  }

  get cookieUser() {
    return this._cookieUser;
  }

  private _isInWelcomeFlow: boolean = false;
  set isInWelcomeFlow(val: boolean) {
    this._isInWelcomeFlow = val;
  }

  get isInWelcomeFlow(): boolean {
    return this._isInWelcomeFlow;
  }

  public storageDetails$: BehaviorSubject<StorageDetails> = new BehaviorSubject(undefined);
  get storageDetails() {
    return this.storageDetails$.getValue();
  }

  set storageDetails(details) {
    this.storageDetails$.next(details);
  }

  public paymentMethods$: BehaviorSubject<any> = new BehaviorSubject(undefined);
  get paymentMethods() {
    return this.paymentMethods$.getValue();
  }

  set paymentMethods(methods) {
    this.paymentMethods$.next(methods);
  }

  public subDetails$: BehaviorSubject<any> = new BehaviorSubject(undefined);
  get subDetails() {
    return this.subDetails$.getValue();
  }

  set subDetails(details) {
    this.subDetails$.next(details);
  }

  public checkEmployeeOrDealerAccess(user) {
    return this.checkEmployeeAccess(user, true) || (!!user?.is_dealer && !!user?.active);
  }

  public checkEmployeeAccess(user, onlyActive?) {
    // With EMPLOYEE_VERIFICATION_ENFORCE on (paeapi sends `employee_verification_enforced`), employee
    // status is the server's verified flag, never the address: Fender doesn't verify emails at sign-up,
    // so anyone can register a @fender.com address. `is_employee` is the inhouse group, which only an
    // admin or the employee email-verify link grants. Off: the address decides, as before — exact staff
    // domain, so lookalikes like x@fender.com.example.net don't count.
    const employee = user?.employee_verification_enforced ? !!user?.is_employee && !user?.employee_verification_required : this.isStaffEmail(user?.email);
    return onlyActive ? employee && !!user?.active : employee;
  }

  private isStaffEmail(email: unknown): boolean {
    const normalized = typeof email === "string" ? email.trim().toLowerCase() : "";
    return ["@fender.com", "@presonus.com"].some((domain) => normalized.endsWith(domain) && normalized.length > domain.length);
  }

  public usePaeapiCheckout() {
    if (!environment.features.employeeTestPaeapiCheckout) return true;
    return this.isEmployee;
  }

  saveAddress(address) {
    return this.http[address.id ? "put" : "post"](environment.paeApiUrl + "user/addresses", JSON.stringify(address), this._helperService.getHttpOptions()).pipe();
  }

  setDefaultAddress(address_id) {
    return this.http.post(environment.apiUrl + "users/set_address_default", JSON.stringify({ address_id: address_id }), this._helperService.getHttpOptions());
  }

  deleteAddress(address_id) {
    return this.http.delete(environment.paeApiUrl + "user/addresses/" + address_id, this._helperService.getHttpOptions()).pipe();
  }

  checkFenderLoginMethod(email, customToken?) {
    return this.http.get(environment.paeApiUrl + "user/fender-user-login-method/" + email, this._helperService.getHttpOptions(true, undefined, customToken)).pipe();
  }

  linkFenderAccount(password) {
    return this.http.post(environment.paeApiUrl + "user/link-fender-account/", JSON.stringify({ password }), this._helperService.getHttpOptions()).pipe();
  }

  uploadDeveloperKey(publicKey: string) {
    return this.http.post(environment.paeApiUrl + "user/developer_key", JSON.stringify({ publicKey }), this._helperService.getHttpOptions()).pipe();
  }

  deletePhoto(type: "profile_photo" | "cover_photo" = "profile_photo") {
    return this.saveUser({ [type === "cover_photo" ? "cover_photo" : "photoURL"]: "" }).pipe(
      map((result) => {
        this.getUserDetails();
        return result;
      })
    );
  }

  getNewNoteworthy() {
    if (this.news) {
      return observableOf(this.news);
    } else {
      return this.http.get(environment.apiUrl + "users/new_noteworthy", this._helperService.getHttpOptions()).pipe(
        map((data: any) => {
          this.news = data;
          return data;
        })
      );
    }
  }

  saveUser(userData, customToken?) {
    if (!userData.id && this.user?.id) userData.id = this.user.id;
    return this.http.put(environment.paeApiUrl + "user/update", JSON.stringify(userData), this._helperService.getHttpOptions(true, false, customToken)).pipe(
      map((result: any) => {
        if (result.success) {
          this.storage.setItem(StorageKeys.USER, result.user);
          this.user = result.user;
        }
        return result;
      })
    );
  }

  createUpdateFenderUser(data) {
    return this.http.post(environment.paeApiUrl + "user/create-fender-user", JSON.stringify(data), this._helperService.getHttpOptions()).pipe(
      map((result: any) => {
        return result;
      })
    );
  }

  // Email-link recovery for a Fender-only account paeapi can't prove (401 FENDER_EMAIL_VERIFY_REQUIRED):
  // `send` emails a one-click link to the account's address (authorized by the Fender access token);
  // the link's /verify-account page calls `confirm` with the signed token, no session needed.
  sendFenderEmailVerification(accessToken: string) {
    return this.http.post(environment.paeApiUrl + "user/fender-email-verify/send", JSON.stringify({ accessToken }), this._helperService.getHttpOptions());
  }

  confirmFenderEmailVerification(token: string) {
    return this.http.post(environment.paeApiUrl + "user/fender-email-verify/confirm", JSON.stringify({ token }), this._helperService.getHttpOptions());
  }

  // Employee email verification: a @fender.com / @presonus.com address proves nothing on its own, so
  // employee benefits wait until the user opens a link sent to that mailbox (paeapi /user/employee-verify).
  sendEmployeeVerification() {
    return this.http.post(environment.paeApiUrl + "user/employee-verify/send", "{}", this._helperService.getHttpOptions());
  }

  confirmEmployeeVerification(token: string) {
    return this.http.post(environment.paeApiUrl + "user/employee-verify/confirm", JSON.stringify({ token }), this._helperService.getHttpOptions());
  }

  // Marks a PreSonus Cognito account's email verified (when it matches a Customer) so a follow-up
  // resetPassword against the PreSonus pool can deliver a code. Always resolves 200 (no account
  // enumeration). See paeapi POST /user/prepare-presonus-reset.
  preparePresonusReset(email) {
    return this.http.post(environment.paeApiUrl + "user/prepare-presonus-reset", JSON.stringify({ email }), this._helperService.getHttpOptions());
  }

  setBadgeUsersMap() {
    if (!this.badgeUsersMap && this.badgeUsers) {
      this.badgeUsersMap = {
        employees: {},
        experts: {},
        community_moderators: {},
        community_wizards: {},
      };
      if (this.badgeUsers.employees && this.badgeUsers.employees.length) {
        this.badgeUsers.employees.forEach((item) => {
          this.badgeUsersMap.employees[item.id] = item;
        });
      }
      if (this.badgeUsers.experts && this.badgeUsers.experts.length) {
        this.badgeUsers.experts.forEach((item) => {
          this.badgeUsersMap.experts[item.id] = item;
        });
      }
      if (this.badgeUsers.community_moderators && this.badgeUsers.community_moderators.length) {
        this.badgeUsers.community_moderators.forEach((item) => {
          this.badgeUsersMap.community_moderators[item.id] = item;
        });
      }
      if (this.badgeUsers.community_wizards && this.badgeUsers.community_wizards.length) {
        this.badgeUsers.community_wizards.forEach((item) => {
          this.badgeUsersMap.community_wizards[item.id] = item;
        });
      }
    }
  }

  getApplicationData() {
    return this.http.get(environment.paeApiUrl + "user/application-data", this._helperService.getHttpOptions()).pipe(
      map((result: any) => {
        this.badgeUsers = result.badgeUsers;
        this.setBadgeUsersMap();
        return result;
      })
    );
  }

  public gettingUserDetails: boolean = false;
  getUserDetails(autoSubscribe = true, customToken?): any {
    const baseUrl = "user";
    // Join the query parameters and return the assembled URL
    let fn = this.http.get(environment.paeApiUrl + baseUrl, this._helperService.getHttpOptions(true, undefined, customToken)).pipe(
      map((data: any) => {
        if (data.shopper_data) this._processShopperData(data.shopper_data);
        data.id = data.id.toString();
        this.storage.setItem(StorageKeys.USER, data);
        this.user = data;
        if (this.user.photoURL) this.user.photoURL = this.user.photoURL + "?" + moment().unix();
        this.gettingUserDetails = false;
        this.userDataRefreshed$.next(this.user);
        return data;
      })
    );
    this.gettingUserDetails = true;
    if (autoSubscribe) {
      fn.subscribe();
    } else {
      return fn;
    }
  }

  forgotPassword(email) {
    return this.http.post(environment.fenderUserServiceApiUrl + "reset-password", JSON.stringify({ email }), this._helperService.getHttpOptions());
  }

  private _processShopperData(shopper_data, allowIndianCardToProcess = false) {
    if (shopper_data["payment-sources"] && shopper_data["payment-sources"]["credit-card-info"] && shopper_data["payment-sources"]["credit-card-info"]["billing-contact-info"]) {
      // if this is true, there is only one card which means they do not send back an array.
      let obj = {};
      for (var i in shopper_data["payment-sources"]["credit-card-info"]) {
        obj[i] = shopper_data["payment-sources"]["credit-card-info"][i];
      }
      let arr = [obj];
      shopper_data["payment-sources"]["credit-card-info"] = arr;
    }
    if (shopper_data["payment-sources"] && shopper_data["payment-sources"]["credit-card-info"]) {
      shopper_data.saved_cards = shopper_data["payment-sources"]["credit-card-info"];
      if (shopper_data.saved_cards && shopper_data.saved_cards.length) {
        // the following line is a hack because bluesnap keeps declining indian saved cards for missing a state, see bluesnap ticket #1812622
        // it filters out any saved cards that have the country India. To revert, just remove everything from .filter on.
        let savedCards = shopper_data.saved_cards;
        if (!allowIndianCardToProcess) {
          savedCards = shopper_data.saved_cards.filter((item) => item["billing-contact-info"].country != "in");
        }

        savedCards.forEach((card) => {
          if (card["credit-card"] && card["credit-card"]["issuing-bank"]) {
            card["credit-card"]["issuing-bank"] = card["credit-card"]["issuing-bank"].replace("&#x29;", ")").replace("&#x28;", "(");
          }
        });
        shopper_data.saved_cards = savedCards;
      }
    }
    if (this._user) shopper_data.email = this._user.email;
    this.shopperData = shopper_data;
  }

  downloadData() {
    return this.http.get(environment.paeApiUrl + "user/download-data", this._helperService.getHttpOptions()).pipe(
      map((data: any) => {
        var blob = new Blob([data.data], { type: "text/html;charset=utf-8" });
        saveAs(blob);
        return data;
      })
    );
  }

  deleteData() {
    return this.http.delete(environment.paeApiUrl + "user", this._helperService.getHttpOptions());
  }

  signOutCognitoUserGlobally() {
    return this.http.get(environment.paeApiUrl + "user/sign-out-cognito-user", this._helperService.getHttpOptions());
  }

  private _countries$: Observable<any> | null = null;
  /**
   * Country list for checkout/forms. Cached for the session (reference data that doesn't change
   * within a session) via shareReplay, so warming it once — e.g. on a page load — makes later reads
   * (opening the sign-up modal) resolve instantly without a second round-trip. Callers only read the
   * result, so sharing the same object is safe.
   */
  listCountries() {
    if (!this._countries$) {
      this._countries$ = this.http.get(environment.paeApiUrl + "checkout/countries", this._helperService.getHttpOptions(false)).pipe(shareReplay(1));
    }
    return this._countries$;
  }

  public stateMap: any = {};
  listStates() {
    return this.http.get(environment.paeApiUrl + "checkout/states", this._helperService.getHttpOptions()).pipe(
      map((result: any) => {
        this.stateMap = result;
        return result;
      })
    );
  }

  /**
   * Two-step file upload against paeapi's /user/upload-file:
   *   1. POST metadata to get a presigned S3 PUT url
   *   2. PUT the bytes directly to S3
   *
   * The observable emits progress events (0-100) during the S3 PUT, then a
   * final "done" event carrying paeapi's step-1 response (uploadUrl, publicUrl,
   * updated user for profile/cover photo variants, etc.).
   */
  uploadFile(method: string, file: Blob, options: { fileName: string; contentType?: string; uuid?: string }): Observable<{ progress: number; done: boolean; response?: any }> {
    const contentType = options.contentType || file.type || "application/octet-stream";
    const body: any = { method, fileName: options.fileName, contentType };
    if (options.uuid) body.uuid = options.uuid;
    return this.http.post<any>(environment.paeApiUrl + "user/upload-file", JSON.stringify(body), this._helperService.getHttpOptions()).pipe(
      switchMap((presign: any) => {
        return this.http
          .put(presign.uploadUrl, file, {
            headers: new HttpHeaders({ "Content-Type": contentType }),
            reportProgress: true,
            observe: "events",
          })
          .pipe(
            map((event: any) => {
              if (event.type === HttpEventType.UploadProgress) {
                const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
                return { progress, done: false } as const;
              } else if (event.type === HttpEventType.Response) {
                return { progress: 100, done: true, response: presign } as const;
              }
              return { progress: 0, done: false } as const;
            })
          );
      })
    );
  }

  /**
   * Zendesk attachments larger than ~4.5 MB cannot travel through API Gateway +
   * Lambda (6 MB payload cap), so this is a three-step S3 staging flow:
   *   1. POST /user/upload-file/zendesk-attachment/url → { uploadUrl, key, contentType }
   *   2. PUT the bytes to the presigned URL
   *   3. POST /user/upload-file/zendesk-attachment/complete → Zendesk envelope
   *
   * The complete response is the same `{ success: true, attachment: { upload: { token } } }`
   * shape the ticket modals already walk.
   *
   * Emits the same `{ progress, done, response? }` shape as {@link uploadFile}
   * so a single template pattern works for both.
   */
  uploadZendeskAttachment(file: Blob, filename: string): Observable<{ progress: number; done: boolean; response?: any }> {
    const options = this._helperService.getHttpOptions();
    return this.http.post<any>(environment.paeApiUrl + "user/upload-file/zendesk-attachment/url", JSON.stringify({ filename, size: file.size }), options).pipe(
      switchMap((presign: any) => {
        const headers = new HttpHeaders({ "Content-Type": presign.contentType || "application/octet-stream" });
        const progress$ = this.http.put(presign.uploadUrl, file, { headers, reportProgress: true, observe: "events" }).pipe(
          filter((event: any) => event.type === HttpEventType.UploadProgress),
          map((event: any) => ({
            progress: event.total ? Math.round((100 * event.loaded) / event.total) : 0,
            done: false as const,
          }))
        );
        const complete$ = this.http
          .post(environment.paeApiUrl + "user/upload-file/zendesk-attachment/complete", JSON.stringify({ key: presign.key, filename }), options)
          .pipe(map((response: any) => ({ progress: 100, done: true as const, response })));
        return concat(progress$, complete$);
      })
    );
  }

  listLanguages() {
    return this.http.get(environment.paeApiUrl + "user/list-languages", this._helperService.getHttpOptions(false)).pipe(
      map((result: any) => {
        result.sort((a, b) => {
          if (a.title < b.title) {
            return -1;
          } else if (a.title > b.title) {
            return 1;
          }
        });
        return result;
      })
    );
  }

  verifyCaptcha(args: { captcha: string; action?: string; version?: string; scoreThreshold?: number }) {
    return this.http.put(environment.paeApiUrl + "user/verify-captcha", JSON.stringify(args), this._helperService.getHttpOptions());
  }

  verifyEmail(email) {
    return this.http.put(environment.paeApiUrl + "user/verify-email", JSON.stringify({ email }), this._helperService.getHttpOptions());
  }

  sendPaymentMethodUpdateEmail() {
    return this.http.get(environment.paeApiUrl + "user/update-payment-method", this._helperService.getHttpOptions());
  }

  saveNotifications(args) {
    return this.http.post(environment.apiUrl + "users/save_notification", JSON.stringify(args), this._helperService.getHttpOptions());
  }

  saveIterableSettings(args) {
    return this.http.post(environment.paeApiUrl + "user/save-iterable-settings", JSON.stringify(args), this._helperService.getHttpOptions());
  }

  getStorageDetails(id?, force?) {
    let userStorage = this.storage.getItem(StorageKeys.USER_STORAGE);
    if (!id && !force && this.storageDetails) {
      return observableOf(this.storageDetails);
    } else if (!id && !force && this.storage.checkTimestamp("user_storage") && userStorage) {
      this.storageDetails = userStorage;
      return observableOf(userStorage);
    } else {
      let url = `${environment.paeApiUrl}user/storage-details`;
      if (id) url += "?userId=" + id;
      return this.http.get(url, this._helperService.getHttpOptions()).pipe(
        map((result: StorageDetails) => {
          if (!id) {
            this.storage.setTimestamp("user_storage", 24);
            this.storage.setItem(StorageKeys.USER_STORAGE, result);
          }
          if (!id) this.storageDetails = result;
          return result;
        })
      );
    }
  }

  getPaymentMethods(force?) {
    let paymentMethods = this.storage.getItem(StorageKeys.USER_PAYMENT_METHODS);
    if (!force && this.paymentMethods) {
      return observableOf(this.paymentMethods);
    } else if (!force && this.storage.checkTimestamp("user_storage") && paymentMethods) {
      this.paymentMethods = paymentMethods;
      return observableOf(paymentMethods);
    } else {
      let url = environment.paeApiUrl + "user/paymentmethods";
      return this.http.get(url, this._helperService.getHttpOptions()).pipe(
        map((result: any) => {
          this.storage.setTimestamp("user_payment_methods", 24);
          this.storage.setItem(StorageKeys.USER_PAYMENT_METHODS, result.payment_methods?.length ? result.payment_methods : []);
          this.paymentMethods = result.payment_methods?.length ? result.payment_methods : [];
          return result;
        })
      );
    }
  }

  getSubscriptionDetails(force?, includeStorageDetails?) {
    force = true;
    let subDetails = this.storage.getItem(StorageKeys.SUBSCRIPTION_DETAILS);
    if (!force && this.subDetails) {
      return observableOf(this.subDetails);
    } else if (!force && this.storage.checkTimestamp("subscription_details") && subDetails) {
      this.subDetails = subDetails;
      return observableOf(subDetails);
    } else {
      let url = environment.paeApiUrl + "user/subscription-details";
      if (includeStorageDetails) url += "?includeStorageDetails=true";
      return this.http.get(url, this._helperService.getHttpOptions()).pipe(
        map((result: any) => {
          this.paymentMethods = result.payment_methods;
          if (includeStorageDetails) this.storageDetails = result.storage_details;
          this.storage.setTimestamp("subscription_details", 24);
          this.storage.setItem(StorageKeys.SUBSCRIPTION_DETAILS, result);
          this.subDetails = result;
          return result;
        })
      );
    }
  }

  getPromotions() {
    return this.http.get(environment.paeApiUrl + "user/promotions", this._helperService.getHttpOptions());
  }

  previewStripeInvoice(priceData) {
    const usePaeapi = this.usePaeapiCheckout();
    const url = usePaeapi ? environment.paeApiUrl + "checkout/preview-invoice" : environment.apiUrl + "stripe/preview_invoice/";
    return this.http.post(url, JSON.stringify(priceData), this._helperService.getHttpOptions()).pipe(map((r: any) => (usePaeapi ? r : r?.data)));
  }

  cancelSubscription(id?) {
    return this.http.get(environment.apiUrl + "users/cancel_subscription/" + (id ? id : ""), this._helperService.getHttpOptions());
  }

  cancelSubscriptionStripe(channel, id?) {
    if (this.usePaeapiCheckout()) {
      return this.http.post(environment.paeApiUrl + "checkout/subscription/cancel", JSON.stringify({ subscription_id: id, source: channel }), this._helperService.getHttpOptions());
    }
    return this.http.get(environment.apiUrl + "stripe/cancel_subscription/" + (id ? id : "") + "/" + channel, this._helperService.getHttpOptions());
  }

  cancelScheduledSubscriptionStripe(channel, id?) {
    return this.http.get(environment.apiUrl + "stripe/cancel_subscription_scheduled/" + (id ? id : "") + "/" + channel, this._helperService.getHttpOptions());
  }

  switchSubscription(switch_to) {
    return this.http.post(environment.apiUrl + "users/switch_subscriptions/", JSON.stringify({ switch_to }), this._helperService.getHttpOptions());
  }

  switchRechargeSubscriptionToHybrid() {
    return this.http.put(environment.paeApiUrl + "user/switch-to-hybrid/", JSON.stringify({}), this._helperService.getHttpOptions());
  }

  getSpherePrices(countryCode?, state?, storageAddon?) {
    return this.http.get(
      environment.apiUrl + "users/sphere_prices/" + (countryCode ? countryCode : "false") + "/" + (state ? state : "false") + "/" + (storageAddon ? storageAddon : ""),
      this._helperService.getHttpOptions()
    );
  }

  getSpherePricesStripe() {
    return this.http.get(environment.paeApiUrl + `stripe/prices`, this._helperService.getHttpOptions());
  }

  getGenericProductPrices(stripeProductId) {
    return this.http.get(environment.paeApiUrl + `stripe/prices?stripeProductId=${stripeProductId}`, this._helperService.getHttpOptions());
  }

  calculateTax(taxData) {
    const url = this.usePaeapiCheckout() ? environment.paeApiUrl + "checkout/calculate-tax" : environment.apiUrl + "avalara/calculate_tax/";
    return this.http.post(url, JSON.stringify(taxData), this._helperService.getHttpOptions());
  }

  stripeSubscribe(checkoutData) {
    if (this.usePaeapiCheckout()) {
      return this.http.post(environment.paeApiUrl + "checkout/subscription", JSON.stringify(checkoutData), this._helperService.getHttpOptions());
    }
    return this.http.post(environment.apiUrl + "stripe/subscription_create/", JSON.stringify(checkoutData), this._helperService.getHttpOptions()).pipe(map((r: any) => r?.data));
  }

  subscriptionResume(subscriptionData) {
    if (this.usePaeapiCheckout()) {
      return this.http.post(environment.paeApiUrl + "checkout/subscription/resume", JSON.stringify(subscriptionData), this._helperService.getHttpOptions());
    }
    return this.http.post(environment.apiUrl + "stripe/subscription_resume_transaction/", JSON.stringify(subscriptionData), this._helperService.getHttpOptions()).pipe(map((r: any) => r?.data));
  }

  stripeSubscribeSchedule(checkoutData) {
    return this.http.post(environment.apiUrl + "stripe/subscription_schedule/", JSON.stringify(checkoutData), this._helperService.getHttpOptions());
  }

  stripeSubscriptionSwitch(checkoutData) {
    if (this.usePaeapiCheckout()) {
      return this.http.post(environment.paeApiUrl + "checkout/subscription/switch", JSON.stringify(checkoutData), this._helperService.getHttpOptions());
    }
    return this.http.post(environment.apiUrl + "stripe/subscription_switch/", JSON.stringify(checkoutData), this._helperService.getHttpOptions()).pipe(map((r: any) => r?.data));
  }

  createProductInvoice(data) {
    if (this.usePaeapiCheckout()) {
      return this.http.post(environment.paeApiUrl + "checkout/product-invoice", JSON.stringify(data), this._helperService.getHttpOptions());
    }
    return this.http.post(environment.apiUrl + "stripe/product_invoice/", JSON.stringify(data), this._helperService.getHttpOptions()).pipe(map((r: any) => r?.data));
  }

  resumeTransaction(data) {
    if (this.usePaeapiCheckout()) {
      return this.http.post(environment.paeApiUrl + "checkout/product-invoice/resume", JSON.stringify(data), this._helperService.getHttpOptions());
    }
    return this.http.post(environment.apiUrl + "stripe/resume_transaction/", JSON.stringify(data), this._helperService.getHttpOptions()).pipe(map((r: any) => r?.data));
  }

  currencyConvert(currencyData) {
    const usePaeapi = this.usePaeapiCheckout();
    const url = usePaeapi ? environment.paeApiUrl + "checkout/currency/convert" : environment.apiUrl + "neutrino/currency_convert/";
    return this.http.post(url, JSON.stringify(currencyData), this._helperService.getHttpOptions()).pipe(map((r: any) => (usePaeapi ? { success: true, data: r } : r)));
  }

  getCurrencyCodeByCountryCode(countryCode) {
    if (!countryCode || typeof countryCode !== "string" || !/^[A-Za-z]{2}$/.test(countryCode)) {
      return of({ data: { locations: [] } });
    }
    const usePaeapi = this.usePaeapiCheckout();
    const url = usePaeapi ? environment.paeApiUrl + "checkout/currency/" + countryCode : environment.apiUrl + "neutrino/currency/" + countryCode;
    return this.http.get(url, this._helperService.getHttpOptions()).pipe(map((r: any) => (usePaeapi ? { data: r } : r)));
  }

  reactivateSubscription(reactivate) {
    if (this.usePaeapiCheckout()) {
      return this.http.post(environment.paeApiUrl + "checkout/subscription/reactivate", JSON.stringify(reactivate), this._helperService.getHttpOptions());
    }
    return this.http.post(environment.apiUrl + "stripe/reactivate_subscription/", JSON.stringify(reactivate), this._helperService.getHttpOptions());
  }

  validateCoupon(coupon) {
    const usePaeapi = this.usePaeapiCheckout();
    const url = usePaeapi ? environment.paeApiUrl + "checkout/validate-coupon" : environment.apiUrl + "stripe/validate_coupon/";
    return this.http.post(url, JSON.stringify(coupon), this._helperService.getHttpOptions()).pipe(map((r: any) => (usePaeapi ? r : r?.data)));
  }

  getActiveOffers() {
    return this.http.get(environment.paeApiUrl + "checkout/active-offers", this._helperService.getHttpOptions());
  }

  getCardClientSecret(cardDetails) {
    return this.http.post(environment.paeApiUrl + "checkout/fsp/client-secret", JSON.stringify(cardDetails), this._helperService.getHttpOptions());
  }

  setDefaultPaymentMethod(paymentMethodId) {
    return this.http.put(environment.paeApiUrl + "user/paymentmethods/", JSON.stringify({ make_default_id: paymentMethodId }), this._helperService.getHttpOptions());
  }

  getPlayPaymentMethods() {
    return this.http.get(environment.paeApiUrl + "checkout/play/paymentmethods", this._helperService.getHttpOptions());
  }

  setPlayDefaultPaymentMethod(paymentMethodId) {
    return this.http.put(environment.paeApiUrl + "checkout/play/paymentmethods", JSON.stringify({ make_default_id: paymentMethodId }), this._helperService.getHttpOptions());
  }

  deletePlayPaymentMethod(paymentMethodId) {
    return this.http.delete(environment.paeApiUrl + "checkout/play/paymentmethods/" + paymentMethodId, this._helperService.getHttpOptions());
  }

  getPlayCardClientSecret(cardDetails) {
    return this.http.post(environment.paeApiUrl + "checkout/play/client-secret", JSON.stringify(cardDetails), this._helperService.getHttpOptions());
  }

  getPlayStripeSubscriptionDetails(subscriptionId: string) {
    return this.http.get(environment.paeApiUrl + "checkout/play/subscription/" + subscriptionId, this._helperService.getHttpOptions());
  }

  updateCreditCardDetails(args) {
    return this.http.post(environment.apiUrl + "users/update_credit_card", JSON.stringify(args), this._helperService.getHttpOptions());
  }

  submitFeedback(args) {
    return this.http.post(environment.paeApiUrl + "user/feedback", JSON.stringify(args), this._helperService.getHttpOptions());
  }

  checkAppVersion() {
    // Fetch version_check.txt as plain text and observe full response to get headers
    return this.http.get("/version_check.txt", {
      responseType: "text",
      observe: "response", // This returns HttpResponse with headers
    });
  }

  getFeedback(limit: any = false, start: any = false, end: any = false, type: any = false) {
    let url = "feedback/index/" + (limit ? limit : false) + "/";
    if (start && end) {
      url += start + "/" + end + "/";
    } else {
      if (start) {
        url += start + "/false/";
      } else if (end) {
        url += "false/" + end + "/";
      }
    }
    if (type) url += type;
    return this.http.get(environment.apiUrl + url, this._helperService.getHttpOptions());
  }

  public loggingOut: boolean = false;
  logout(skipApiLogout?) {
    this.loggingOut = true;
    const finish = () => {
      this.user = false;
      this.userSubscription = undefined;
      this.subscriptionProvider = undefined;
      this.userMetadata = {
        products_seen: {},
        sphere_announcements: {},
      };
      this.shopperData = undefined;
      this._helperService.token = false;
      this._helperService.idToken = false;
      this._helperService.accessToken = false;
      this._helperService.refreshToken = false;
      this._helperService.cookieToken = false;
      this.loggingOut$.next("finished");
      this.loggingOut = false;
      for (var i in StorageKeys) {
        if (!this.storage.persistDataOnLogout[i]) this.storage.removeItem(StorageKeys[i]);
      }
    };
    finish();
    return of(true);
  }

  searchUser(email) {
    return this.http.get(environment.paeApiUrl + "user/search/" + encodeURIComponent(email), this._helperService.getHttpOptions()).pipe(
      map((result: any) => {
        return result;
      })
    );
  }

  sso(args, accessToken) {
    let url = environment.paeApiUrl;
    return this.http.post(url + "sso", JSON.stringify(args), this._helperService.getPaeApiOptions(accessToken));
  }

  getCountry() {
    return this.http.get(environment.paeApiUrl + "request-country", this._helperService.getPaeApiOptions());
  }

  getAddresses() {
    return this.http.get(environment.paeApiUrl + "user/addresses", this._helperService.getHttpOptions());
  }

  authenticatePresonusUser(email, password) {
    return this.http.post(environment.paeApiUrl + "user/authenticate-presonus-user", JSON.stringify({ email, password }), this._helperService.getHttpOptions());
  }

  removeSocialLogin(newPassword) {
    return this.http.post(environment.paeApiUrl + "user/remove-social-login", JSON.stringify({ password: newPassword }), this._helperService.getHttpOptions());
  }

  validateFenderMultipass(email, fcId) {
    let args = {
      "Content-Type": "application/json",
      "x-api-key": environment.fenderShopify.fenderOrdersApiKey,
    };
    let headers = { headers: new HttpHeaders(args) };
    return this.http.post(
      environment.fenderShopify.fenderOrdersApiUrl,
      {
        requestType: "checkCustomerAndUpdateMultipassIdentifierWithoutSiteId",
        siteID: "FENDER_US",
        data: {
          customerQuery: {
            email: email,
          },
          multipassIdentifier: fcId,
        },
      },
      headers
    );
  }

  get profilePicture(): string {
    if (!this.profilePhotoCacheBuster) {
      this.profilePhotoCacheBuster = Date.now();
    }
    const picture = this._user.photoURL ? this._user.photoURL + (this._user.lastUpdated ? `?${this._user.lastUpdated}` : `?${this.profilePhotoCacheBuster}`) : "./assets/images/default_avatar.jpg";
    return picture;
  }
}
